<template>
  <div class="agent-commission">
    <div class="commission-header">
      <h2>{{ $t('Agent Report') }}</h2>
      <div class="date-filter">
        <input type="date" v-model="startDate">
        <span class="date-separator">{{ $t('To') }}</span>
        <input type="date" v-model="endDate">
        <button class="filter-btn" @click="queryReport">{{ $t('Query') }}</button>
        <button class="export-btn" :disabled="exporting" @click="exportReport">
          {{ exporting ? $t('Exporting') : $t('Export Excel') }}
        </button>
      </div>
    </div>

    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{{ $t('Username') }}</th>
            <th>{{ $t('Downline Username') }}</th>
            <th>{{ $t('Downline Level') }}</th>
            <th>{{ $t('Commission Amount') }}</th>
            <th>{{ $t('Game Name') }}</th>
            <th>{{ $t('Bet Amount') }}</th>
            <th>{{ $t('Win/Loss Result') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.id">
            <td>{{ row.agent_username }}</td>
            <td>{{ row.downline_username }}</td>
            <td>{{ row.level }}</td>
            <td>{{ row.commission | vnd }}</td>
            <td>{{ row.game_name }}</td>
            <td>{{ row.bet_amount | vnd }}</td>
            <td>{{ resultText(row.result) }}</td>
          </tr>
        </tbody>
      </table>
      <div class="no-data" v-if="rows.length === 0">{{ $t('No commission records yet') }}</div>
    </div>

    <div class="totals-bar">
      <span>{{ $t('Total Bet Amount') }}: {{ totals.bet_amount | vnd }}</span>
      <span>{{ $t('Total Commission Amount') }}: {{ totals.commission | vnd }}</span>
    </div>

    <div class="pagination" v-if="totalPages > 1">
      <button class="page-btn" @click="changePage(currentPage - 1)" :disabled="currentPage === 1">{{ $t('Previous Page') }}</button>
      <span class="page-info">{{ $t('Page') }} {{ currentPage }} / {{ totalPages }}</span>
      <button class="page-btn" @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">{{ $t('Next Page') }}</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'agentCommission',
  data() {
    return {
      startDate: '',
      endDate: '',
      rows: [],
      totals: { bet_amount: 0, commission: 0 },
      currentPage: 1,
      totalPages: 1,
      exporting: false,
    };
  },
  created() {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    this.startDate = sevenDaysAgo.toISOString().split('T')[0];
    this.endDate = today.toISOString().split('T')[0];
    this.getReport();
  },
  methods: {
    resultText(result) {
      if (result === '赢' || result === 'win' || result === 'Win') return this.$t('Win');
      return this.$t('Loss');
    },
    queryReport() {
      this.currentPage = 1;
      this.getReport();
    },
    getReport() {
      const params = {
        page: this.currentPage,
        start_date: this.startDate,
        end_date: this.endDate,
      };
      this.$apiFun.get('/api/agent/report', params).then((res) => {
        if (res && res.code === 200 && res.data) {
          this.rows = res.data.list || [];
          this.totalPages = res.data.last_page || 1;
          this.totals = res.data.totals || { bet_amount: 0, commission: 0 };
        }
      }).catch(() => {
        this.rows = [];
      });
    },
    exportReport() {
      if (this.exporting) return;
      this.exporting = true;
      const params = {
        start_date: this.startDate,
        end_date: this.endDate,
      };
      this.$apiFun.get('/api/agent/report/export', params, {
        responseType: 'blob'
      }).then((blob) => {
        const file = blob instanceof Blob
          ? blob
          : new Blob([blob], { type: 'application/vnd.ms-excel;charset=utf-8' });
        const url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'agent_commission_' + new Date().toISOString().slice(0, 10) + '.xls';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.exporting = false;
      }).catch(() => {
        this.exporting = false;
      });
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.getReport();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.agent-commission {
  padding: 16px;
  color: #352b24;
}

.commission-header h2 {
  margin: 0 0 12px;
  font-size: 16px;
}

.date-filter {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;

  input {
    height: 36px;
    border: 1px solid #ead1b2;
    border-radius: 8px;
    padding: 0 8px;
    background: #fff8ef;
  }

  .filter-btn,
  .export-btn {
    height: 36px;
    padding: 0 12px;
    border: none;
    border-radius: 8px;
    background: linear-gradient(135deg, #ffe082 0%, #d1a03e 50%, #b8860b 100%);
    color: #4a2e00;
    font-weight: 700;
  }

  .export-btn {
    background: linear-gradient(135deg, #4caf7a 0%, #238b57 100%);
    color: #fff;

    &:disabled {
      opacity: 0.6;
    }
  }
}

.table-wrap {
  overflow-x: auto;
  border: 1px solid #eadfd3;
  border-radius: 12px;
}

table {
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
  font-size: 12px;

  th, td {
    padding: 8px 6px;
    text-align: center;
    border-bottom: 1px solid #eadfd3;
    white-space: nowrap;
  }

  th {
    background: #fff8ef;
    color: #8f501f;
  }
}

.no-data {
  text-align: center;
  color: #8a6a4a;
  padding: 24px 12px;
}

.totals-bar {
  margin-top: 12px;
  background: #fff8ef;
  border: 1px solid #ead1b2;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 700;
  color: #8f501f;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 12px;
}

.page-btn {
  border: 1px solid #d9b98f;
  background: #fff8ef;
  color: #8f501f;
  border-radius: 8px;
  padding: 6px 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/agent/agentCommission.vue